import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { format } from 'date-fns';

export default function usePatientSectionData(Patientid: string, fields: string, sectionKey: string) {
    const [data, setData] = useState<any>(null);
    const [formData, setFormData] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const fetchSection = async () => {
            setLoading(true);
            try {
                const response = await ApiServices.getPatientProfile(Patientid, fields);
                const sectionData = response?.patient?.[fields];
                if (sectionData) {
                    setData(sectionData);
                    setFormData({ ...sectionData });
                } else {

                }
            } catch {
                toast.error(`Failed to fetch ${sectionKey} data.`);
            } finally {
                setLoading(false);
            }
        };

        if (Patientid && fields) fetchSection();
    }, [Patientid, fields, sectionKey]);

    const handleInputChange = (key: string, value: string | boolean) => {
        setFormData((prev: any) => {
            const updated = { ...prev, [key]: value };
            const changed = Object.keys(updated).some(
                (k) => (updated[k] ?? '') !== (data?.[k] ?? '')
            );
            setHasChanges(changed);

            return updated;
        });
    };

    const handleSave = async () => {
        const updatedFields: any = {};
        Object.keys(formData).forEach((key) => {
            let value = formData[key];

            if (key === 'dateOfInitialRegistration' && value) {
                try {
                    value = format(new Date(value), 'dd/MM/yyyy');
                } catch {
                    console.warn('Invalid date format on save');
                }
            }

            if (value !== data?.[key]) {
                updatedFields[key] = value;
            }
        });

        if (Object.keys(updatedFields).length === 0) {
            toast.info('No changes made.');
            setEditMode(false);
            return;
        }

        try {
            setUpdating(true);
            await ApiServices.modifyPatientProfile(Patientid, {
                [sectionKey]: { ...data, ...updatedFields },
            });
            toast.success(`${sectionKey} updated.`);
            setData((prev: any) => ({ ...prev, ...updatedFields }));
            setEditMode(false);
        } catch {
            toast.error(`Failed to update ${sectionKey}.`);
        } finally {
            setUpdating(false);
        }
        setEditMode(false);
        setHasChanges(false);
    };

    const handleCancel = () => {
        const restoredData: any = {};
        Object.keys(formData).forEach((key) => {
            restoredData[key] = data?.[key] || '';
        });
        setFormData(restoredData);
        setEditMode(false);
        setHasChanges(false);
    };

    return {
        data,
        formData,
        loading,
        updating,
        editMode,
        setEditMode,
        handleInputChange,
        handleSave,
        handleCancel,
        hasChanges,
    };
}
